import React from "react";
import {
  Box, Typography, Grid, Card, Chip, List, ListItem,
  ListItemIcon, ListItemText,
} from "@mui/material";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

const ROLES = [
  {
    name: "tenant-admin",
    label: "Tenant Admin",
    icon: <AdminPanelSettingsOutlinedIcon sx={{ color: "#0d47a1" }} />,
    chipStyle: { bg: "#e3f2fd", color: "#0d47a1" },
    description:
      "Organisation administrator. Invites users and assigns tenant roles. Created as the first user when a tenant is onboarded.",
    can: [
      "Invite users and assign roles",
      "Edit / disable / delete users",
      "View Gate 0 summary",
    ],
    cannot: [
      "Onboard applications or chat (unless also enterprise-ai-owner)",
      "Access platform admin functions",
    ],
  },
  {
    name: "enterprise-ai-owner",
    label: "Enterprise AI Owner",
    icon: <EngineeringOutlinedIcon sx={{ color: "#1b5e20" }} />,
    chipStyle: { bg: "#e8f5e9", color: "#1b5e20" },
    description:
      "Primary Gate 0 actor. Onboards applications, configures frameworks, and chats with the intake bot. Summary and later gates are view-only unless additional roles are assigned.",
    can: [
      "Onboard / create applications",
      "Set Gate 0 and framework configuration",
      "Chat with intake chatbot (view + write)",
      "View summary",
      "Assign users to projects",
    ],
    cannot: [
      "Invite users or manage tenant roles (tenant-admin)",
      "Write on Gate 1 Risk / Test / Execution (unless also tevv-lead or risk-reviewer)",
      "Access platform admin functions",
    ],
  },
  {
    name: "tevv-lead",
    label: "TEVV Lead",
    icon: <ManageAccountsOutlinedIcon sx={{ color: "#6a1b9a" }} />,
    chipStyle: { bg: "#f3e5f5", color: "#6a1b9a" },
    description:
      "TEVV execution lead. Phase 1: Summary view only. Later: Test and Execution write.",
    can: [
      "View Gate 0 summary",
      "View Gate 1 metrics / risk (later)",
      "Write Test & Execution (later gates)",
    ],
    cannot: [
      "Onboard applications or chat (unless also enterprise-ai-owner)",
      "Manage users",
    ],
  },
  {
    name: "risk-reviewer",
    label: "Risk Reviewer",
    icon: <GavelOutlinedIcon sx={{ color: "#b71c1c" }} />,
    chipStyle: { bg: "#fce4ec", color: "#b71c1c" },
    description:
      "Risk and compliance reviewer. Phase 1: Summary view. Later: Risk register write.",
    can: [
      "View Gate 0 summary",
      "Write Risk module (later)",
      "View Execution / Reports (later)",
    ],
    cannot: [
      "Chat or onboard applications",
      "Manage users",
    ],
  },
  {
    name: "security-reviewer",
    label: "Security Reviewer",
    icon: <SecurityOutlinedIcon sx={{ color: "#f57f17" }} />,
    chipStyle: { bg: "#fff8e1", color: "#f57f17" },
    description:
      "Security / privacy reviewer. Phase 1: Summary view only.",
    can: [
      "View Gate 0 summary",
      "View Execution / Reports (later)",
    ],
    cannot: [
      "Chat or onboard applications",
      "Manage users",
    ],
  },
  {
    name: "head-of-quality",
    label: "Head of Quality",
    icon: <VerifiedUserOutlinedIcon sx={{ color: "#e65100" }} />,
    chipStyle: { bg: "#fff3e0", color: "#e65100" },
    description:
      "Program-accountable TEVV leader. Phase 1: Summary view. Later: gate decisions and pack approvals.",
    can: [
      "View Gate 0 summary",
      "View Execution / Reports (later)",
    ],
    cannot: [
      "Chat or onboard applications",
      "Manage users",
    ],
  },
  {
    name: "platform-admin",
    label: "Platform Admin",
    icon: <VisibilityOutlinedIcon sx={{ color: "#1565c0" }} />,
    chipStyle: { bg: "#e3f2fd", color: "#1565c0" },
    description:
      "Lives in the platform realm. Manages tenants and licences. May view summary for support; no tenant Chat write.",
    can: [
      "Manage tenants and licences",
      "View Gate 0 summary (support)",
    ],
    cannot: [
      "Onboard apps or chat inside a tenant as write user",
    ],
  },
];

export default function ClientRolesPage() {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f9fafb", px: 4, py: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Roles</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Phase 1 roles. Users may hold multiple roles — permissions are the union of all assigned roles.
      </Typography>

      <Grid container spacing={3}>
        {ROLES.map((role) => (
          <Grid item xs={12} md={6} key={role.name}>
            <Card elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: 2, p: 3, bgcolor: "#fff", height: "100%" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                {role.icon}
                <Typography fontWeight={700} fontSize="15px">{role.label}</Typography>
                <Chip
                  label={role.name}
                  size="small"
                  sx={{ bgcolor: role.chipStyle.bg, color: role.chipStyle.color, fontWeight: 700, fontSize: "11px" }}
                />
              </Box>

              <Typography fontSize="13px" color="text.secondary" mb={2}>
                {role.description}
              </Typography>

              <Typography fontWeight={600} fontSize="12px" color="#344054" mb={0.5}>Can do:</Typography>
              <List dense disablePadding sx={{ mb: 1.5 }}>
                {role.can.map((item) => (
                  <ListItem key={item} dense disableGutters sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#2e7d32" }} />
                    </ListItemIcon>
                    <ListItemText primary={item} primaryTypographyProps={{ fontSize: "12px" }} />
                  </ListItem>
                ))}
              </List>

              <Typography fontWeight={600} fontSize="12px" color="#344054" mb={0.5}>Cannot:</Typography>
              <List dense disablePadding>
                {role.cannot.map((item) => (
                  <ListItem key={item} dense disableGutters sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <RemoveCircleOutlineIcon sx={{ fontSize: 16, color: "#9e9e9e" }} />
                    </ListItemIcon>
                    <ListItemText primary={item} primaryTypographyProps={{ fontSize: "12px", color: "text.secondary" }} />
                  </ListItem>
                ))}
              </List>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
